import React from 'react';
import { Link } from 'react-router-dom';
import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TransportRouteDetailDto } from '../../../types/transport';
import { formatCurrencyLocalized } from '../../../utils/currency';
import { formatDateEuropean } from '../../../utils/dateUtils';
import { truckFreightAmount } from '../../../utils/truckFreightAmount';
import { truckDeliveryDate, truckLoadingDate } from '../../../utils/truckDates';
import { getTransportLabel } from '../../../utils/transportLabel';
import {
  TRANSPORT_STATUS_DISPLAY_COLORS,
  getTransportStatusDisplay,
  getTransportStatusDisplayTranslationKey,
} from '../../transports/transportRouteViews';
import { deriveTransportKind, transportKindLabelKey } from '../../transports/transportDirection';
import { HiddenValue, InfoList } from './OrderProcessParts';

export { activeOrderTransports, outgoingOrderTransports } from './orderProcessFacts';

/**
 * Read-only details of the trucks linked to the order (makieta „Szczegóły
 * zlecenia transportowego”). The cost is the whole truck's freight and only
 * for roles with transport costs; the ZK share is in the margin explanation.
 */
export default function OrderTransportSummary({
  transports,
  canViewCosts,
  hideCost = false,
}: {
  transports: TransportRouteDetailDto[];
  canViewCosts: boolean;
  /** Leave the freight row out altogether (no „koszt widzi administrator” either). */
  hideCost?: boolean;
}) {
  const { t, i18n } = useTranslation(['orders', 'transport']);
  // The truck's rate as saved on the transport (not the legacy transportCost
  // column, which stays 0 for a truck priced by its rate).
  const formatTruckFreight = (transport: TransportRouteDetailDto) => {
    const amount = truckFreightAmount(transport);
    return amount == null ? '—' : formatCurrencyLocalized(amount, transport.currency || 'PLN', i18n.language);
  };
  return (
    <div className="zk-col" data-testid="zk-transport-summary">
      {transports.map((transport) => {
        const statusKey = getTransportStatusDisplay(transport.status);
        const kind = deriveTransportKind({
          direction: transport.direction ?? null,
          customerOrderCount: transport.routeOrders?.length ?? 0,
          supplierOrderCount: transport.supplierOrders?.length ?? 0,
        });
        const loading = truckLoadingDate(transport);
        const delivery = truckDeliveryDate(transport);
        return (
          <InfoList
            key={transport.id}
            rows={[
              [t('orders:process.transport.number'), (
                <Link to={`/app/transports/${encodeURIComponent(transport.id)}`}>
                  {getTransportLabel(transport, t('orders:process.transport.unnamed'))}
                </Link>
              )],
              [t('orders:process.transport.kind'), t(transportKindLabelKey(kind))],
              // Same names and dates as the truck card (transport:fields).
              [t('transport:fields.loadingDate'), loading ? formatDateEuropean(loading) : t('orders:process.schedule.noDate')],
              [t(transport.actualDelivery ? 'transport:fields.actualDelivery' : 'transport:fields.estimatedDelivery'), delivery ? formatDateEuropean(delivery) : t('orders:process.schedule.noDate')],
              [t('orders:process.transport.carrier'), transport.transportCompany || t('orders:process.transport.carrierMissing')],
              ...(hideCost ? [] : [[t('orders:process.transport.cost'), canViewCosts
                ? formatTruckFreight(transport)
                : <HiddenValue reason="costs" />] as [React.ReactNode, React.ReactNode]]),
              [t('orders:process.transport.status'), (
                <Tag color={TRANSPORT_STATUS_DISPLAY_COLORS[statusKey]}>{t(getTransportStatusDisplayTranslationKey(transport.status))}</Tag>
              )],
            ]}
          />
        );
      })}
    </div>
  );
}
